import React, { FC, ReactElement, useContext, useState, SyntheticEvent } from 'react';
import { Snackbar, SnackbarCloseReason } from '@material-ui/core';
import MuiAlert, { Color } from '@material-ui/lab/Alert';
import {
  NotificationsProviderInterface,
  NotifyParams
} from './interfaces/notificationsProvider.interface';

interface Props {
  children: ReactElement;
}

export const NotificationsContext = React.createContext<NotificationsProviderInterface>({
  notify: () => {}
});

export const NotificationsProvider: FC<Props> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Color | undefined>('error');

  const notify = React.useCallback(({ severity, message }: NotifyParams) => {
    setOpen(true);
    setMessage(message);
    setSeverity(severity);
  }, []);

  const closeNotification = React.useCallback(
    (event: SyntheticEvent, reason?: SnackbarCloseReason) => {
      if (reason === 'clickaway') {
        return;
      }

      setOpen(false);
    },
    []
  );

  const value = React.useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationsContext.Provider value={value}>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        autoHideDuration={5000}
        onClose={closeNotification}
        open={open}
      >
        <MuiAlert onClose={closeNotification} severity={severity} variant="filled">
          {message}
        </MuiAlert>
      </Snackbar>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationsContext);

  if (!context) {
    throw Error('useNotification must be used within NotificationsProvider');
  }

  return context;
};
